import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Content, PageWithHeader } from '@backstage/core-components';
import {
  Box,
  Button,
  Grid,
  Icon,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import BackButton from '../utils/backButton';
import { AgentCard } from './components/AgentCard';
import { useAiStoreAssistants } from './hooks/useAiStoreAssistants';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    width: '100%',
  },
  container: {
    width: '100%',
    maxWidth: 1280,
    margin: '0 auto',
    padding: '12px 16px 18px',
  },
  introTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    lineHeight: '1.12',
    marginBottom: 12,
  },
  introSubtitle: {
    fontSize: 14,
    lineHeight: '1.35',
    color: theme.palette.text.tertiary,
    marginBottom: 22,
    maxWidth: 940,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 16,
    },
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 8,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  topBarContent: {
    flex: 1,
  },
  viewAllButton: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'none',
    minWidth: 'auto',
    padding: '10px 16px',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    lineHeight: '20px',
    borderRadius: 4,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
      boxShadow: 'none',
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    '& .MuiButton-endIcon': {
      marginLeft: 0,
    },
    '& .MuiIcon-root': {
      fontSize: 16,
      width: 16,
      height: 16,
      lineHeight: 1,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'center',
    },
  },
  cardGrid: {
    marginTop: 8,
  },
  emptyState: {
    border: `1px dashed ${theme.palette.border}`,
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    color: theme.palette.text.tertiary,
    background: theme.palette.background.paper,
  },
  buttonsWrapper: {
    display: 'flex',
    gap: 16,
    marginBottom: 24,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const AiStoreLanding = () => {
  const classes = useStyles();
  const { agents, loading } = useAiStoreAssistants();

  const latestCards = useMemo(() => agents.slice(0, 3), [agents]);

  return (
    <PageWithHeader title="AI Store" themeId="home">
      <Content>
        <div className={classes.buttonsWrapper}>
          <BackButton />
        </div>
        <div className={classes.content}>
          <div className={classes.container}>
            <Box className={classes.topBar}>
              <Box className={classes.topBarContent}>
                <Typography className={classes.introTitle}>Agents</Typography>
                <Typography className={classes.introSubtitle}>
                  Discover Agents that help automate tasks, generate content, and
                  improve productivity across your work.
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/ai-store/agents"
                variant="contained"
                color="primary"
                className={classes.viewAllButton}
                disableElevation
                endIcon={<Icon fontSize="small">arrow_outward</Icon>}
              >
                View all Agents
              </Button>
            </Box>

            {loading && (
              <Box className={classes.emptyState}>Loading latest agents...</Box>
            )}

            {!loading && latestCards.length === 0 && (
              <Box className={classes.emptyState}>No agents available yet.</Box>
            )}

            {!loading && latestCards.length > 0 && (
              <Grid container spacing={2} className={classes.cardGrid}>
                {latestCards.map(agent => (
                  <Grid item xs={12} md={4} key={agent.id || agent.title}>
                    <AgentCard {...agent} />
                  </Grid>
                ))}
              </Grid>
            )}
          </div>
        </div>
      </Content>
    </PageWithHeader>
  );
};

export default AiStoreLanding;
